import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserContext } from "../../context/user_context/UserContext";
import { useOrganizationContext } from "../../context/organization_context/OrganizationContext";

export const ProtectedRoute = ({ children }) => {
  const { firebaseUser } = useUserContext();
  const { organizationRoles } = useOrganizationContext();
  const location = useLocation();
  const navigate = useNavigate();
  const permissionChecked = useRef(false);
  const unrestrictedRoutes = ['/home', '/profile', '/organization', '/subscription', '/settings'];

  useEffect(() => {
    if (permissionChecked.current) return;
    permissionChecked.current = true;
    if (unrestrictedRoutes.includes(location.pathname) || 
        /^\/performance-reviews\/[^/]+$/.test(location.pathname)) {
      return;
    }

    // Get user's role and permissions
    const userRole = firebaseUser?.role;
    const rolePermissions = organizationRoles?.[userRole];

    if (!rolePermissions) {
      navigate('/home', { replace: true });
      return;
    }

    // Map routes to their corresponding permission keys
    const routePermissionMap = {
      '/assets': 'assets',
      '/biziboard': 'biziboard',
      '/bizidocs': 'bizidocs',
      '/calendar': 'calendar',
      '/customers': 'customers',
      '/departments': 'departments',
      '/employees': 'employees',
      '/inventory': 'inventory',
      '/inventory-orders': 'inventory_orders',
      '/invoices': 'invoices',
      '/locations': 'locations',
      '/performance-reviews': 'performance_reviews',
      '/products': 'products',
      '/review-time-clocks': 'review_time_clocks',
      '/roles': 'roles',
      '/time-clock': 'time_clock',
      '/unit-of-measure': 'unit_of_measure',
      '/suppliers': 'suppliers'
    };

    // Find the matching permission key for the current route
    const permissionKey = Object.entries(routePermissionMap).find(([route]) => 
      location.pathname.startsWith(route)
    )?.[1];

    if (permissionKey) {
      const hasPermission = 
        rolePermissions[permissionKey]?.read === true 
        || rolePermissions[permissionKey]?.read_all_reviews === true
        || rolePermissions[permissionKey]?.read_and_write_all_reviews === true
        || rolePermissions[permissionKey]?.read_and_write_reviews_for_managed_employees === true
        || rolePermissions[permissionKey]?.read_all_time_clocks === true
        || rolePermissions[permissionKey]?.read_and_write_all_time_clocks === true
        || rolePermissions[permissionKey]?.read_and_write_time_clocks_for_managed_employees === true;

      if (!hasPermission) navigate('/home', { replace: true });
    }
  }, [firebaseUser, organizationRoles, location.pathname, navigate, unrestrictedRoutes]);

  return <>{children}</>;
};
